<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'custom']);

const { t } = useI18n();

const isOpen = ref(false);
const localRule = ref(props.modelValue ? { ...props.modelValue } : null);

const frequencyOptions = [
  { value: 'minutely', label: t('RECURRENCE.FREQUENCY.MINUTELY') },
  { value: 'hourly', label: t('RECURRENCE.FREQUENCY.HOURLY') },
  { value: 'daily', label: t('RECURRENCE.FREQUENCY.DAILY') },
  { value: 'weekly', label: t('RECURRENCE.FREQUENCY.WEEKLY') },
  { value: 'monthly', label: t('RECURRENCE.FREQUENCY.MONTHLY') },
  { value: 'yearly', label: t('RECURRENCE.FREQUENCY.YEARLY') },
];

const unitOptions = computed(() => {
  const map = {
    minutely: { value: 'minutely', label: t('RECURRENCE.UNIT.MINUTES') },
    hourly: { value: 'hourly', label: t('RECURRENCE.UNIT.HOURS') },
    daily: { value: 'daily', label: t('RECURRENCE.UNIT.DAYS') },
    weekly: { value: 'weekly', label: t('RECURRENCE.UNIT.WEEKS') },
    monthly: { value: 'monthly', label: t('RECURRENCE.UNIT.MONTHS') },
    yearly: { value: 'yearly', label: t('RECURRENCE.UNIT.YEARS') },
  };
  return map[selectedFrequency.value] || map.daily;
});

const endTypeOptions = [
  { value: 'never', label: t('RECURRENCE.END_NEVER') },
  { value: 'on_date', label: t('RECURRENCE.END_ON_DATE') },
  { value: 'after_count', label: t('RECURRENCE.END_AFTER_COUNT') },
];

const monthlyTypeOptions = [
  { value: 'day_of_month', label: t('RECURRENCE.MONTHLY_TYPE.DAY_OF_MONTH') },
  { value: 'day_of_week', label: t('RECURRENCE.MONTHLY_TYPE.DAY_OF_WEEK') },
];

const DAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_KEYS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const shortDayName = index => t(`RECURRENCE.DAYS_SHORT.${DAY_KEYS[index] ?? 'SUN'}`);
const fullDayName = index => t(`RECURRENCE.DAY_NAMES_FULL.${DAY_KEYS[index] ?? 'SUN'}`);
const monthName = index => t(`RECURRENCE.MONTHS.${MONTH_KEYS[index] ?? 'JAN'}`);

const weekDays = DAY_KEYS.map((key, index) => ({
  value: index,
  label: shortDayName(index),
}));

const selectedFrequency = computed(() => localRule.value?.frequency || 'daily');
const selectedEndType = computed(() => localRule.value?.end_type || 'never');
const selectedMonthlyType = computed(() => localRule.value?.monthly_type || 'day_of_month');

const formatIntervalText = (frequency, interval) => {
  const map = {
    minutely: t('RECURRENCE.FREQUENCY.MINUTELY', { interval }),
    hourly: t('RECURRENCE.FREQUENCY.HOURLY', { interval }),
    daily: interval === 1 ? t('RECURRENCE.FREQUENCY.DAILY') : t('RECURRENCE.FREQUENCY.DAILY_INTERVAL', { interval }),
    weekly: interval === 1 ? t('RECURRENCE.FREQUENCY.WEEKLY') : t('RECURRENCE.FREQUENCY.WEEKLY_INTERVAL', { interval }),
    monthly: interval === 1 ? t('RECURRENCE.FREQUENCY.MONTHLY') : t('RECURRENCE.FREQUENCY.MONTHLY_INTERVAL', { interval }),
    yearly: interval === 1 ? t('RECURRENCE.FREQUENCY.YEARLY') : t('RECURRENCE.FREQUENCY.YEARLY_INTERVAL', { interval }),
  };
  return map[frequency] || '';
};

const ruleDescription = computed(() => {
  if (!localRule.value) return t('RECURRENCE.NONE');

  const freq = localRule.value.frequency;
  const interval = localRule.value.interval || 1;

  let desc = formatIntervalText(freq, interval);

  if (freq === 'weekly' && (localRule.value.week_days || []).length > 0) {
    const days = localRule.value.week_days.map(d => shortDayName(d)).join(', ');
    desc += `, ${t('RECURRENCE.ON_DAYS', { days })}`;
  } else if (freq === 'monthly') {
    if (localRule.value.monthly_type === 'day_of_week') {
      const week = localRule.value.monthly_week === -1 ? t('RECURRENCE.WEEKS.LAST') : t('RECURRENCE.WEEK_NUMBER', { number: localRule.value.monthly_week });
      const weekday = fullDayName(localRule.value.monthly_weekday);
      desc += `, ${t('RECURRENCE.ON_WEEKDAY_OF_MONTH', { week, weekday })}`;
    } else {
      desc += `, ${t('RECURRENCE.ON_DAY_OF_MONTH', { day: localRule.value.month_day })}`;
    }
  } else if (freq === 'yearly') {
    desc += `, ${t('RECURRENCE.ON_DATE_YEARLY', { month: monthName(localRule.value.year_month - 1), day: localRule.value.year_day })}`;
  }

  const endType = localRule.value.end_type;
  if (endType === 'on_date') {
    desc += `, ${t('RECURRENCE.UNTIL', { date: localRule.value.end_date })}`;
  } else if (endType === 'after_count') {
    desc += `, ${t('RECURRENCE.AFTER_OCCURRENCES', { count: localRule.value.end_count })}`;
  }

  return desc;
});

const updateFrequency = value => {
  const existing = localRule.value || {};
  localRule.value = {
    ...existing,
    frequency: value,
    interval: existing.interval || 1,
    end_type: existing.end_type || 'never',
  };
  if (value !== 'weekly') delete localRule.value.week_days;
  if (value !== 'monthly') {
    delete localRule.value.monthly_type;
    delete localRule.value.monthly_week;
    delete localRule.value.monthly_weekday;
    delete localRule.value.month_day;
  }
  if (value !== 'yearly') {
    delete localRule.value.year_month;
    delete localRule.value.year_day;
  }
  if (value === 'monthly' && localRule.value.monthly_type == null) {
    localRule.value.monthly_type = 'day_of_month';
    localRule.value.month_day = existing.month_day || 1;
  }
  if (value === 'yearly') {
    if (localRule.value.year_month == null) localRule.value.year_month = 1;
    if (localRule.value.year_day == null) localRule.value.year_day = 1;
  }
  emit('update:modelValue', localRule.value);
};

const updateEndType = value => {
  localRule.value = { ...localRule.value, end_type: value };
  if (value !== 'on_date') delete localRule.value.end_date;
  if (value !== 'after_count') delete localRule.value.end_count;
  emit('update:modelValue', localRule.value);
};

const updateMonthlyType = value => {
  localRule.value = { ...localRule.value, monthly_type: value };
  if (value !== 'day_of_week') {
    delete localRule.value.monthly_week;
    delete localRule.value.monthly_weekday;
  } else {
    delete localRule.value.month_day;
  }
  emit('update:modelValue', localRule.value);
};

const updateInterval = value => {
  localRule.value = { ...localRule.value, interval: Math.max(1, parseInt(value) || 1) };
  emit('update:modelValue', localRule.value);
};

const updateWeekDays = value => {
  localRule.value = { ...localRule.value, week_days: value };
  emit('update:modelValue', localRule.value);
};

const updateMonthDay = value => {
  localRule.value = { ...localRule.value, month_day: Math.max(1, Math.min(31, parseInt(value) || 1)) };
  emit('update:modelValue', localRule.value);
};

const updateMonthlyWeek = value => {
  localRule.value = { ...localRule.value, monthly_week: parseInt(value) };
  emit('update:modelValue', localRule.value);
};

const updateMonthlyWeekday = value => {
  localRule.value = { ...localRule.value, monthly_weekday: Math.max(0, Math.min(6, parseInt(value) || 0)) };
  emit('update:modelValue', localRule.value);
};

const updateYearMonth = value => {
  localRule.value = { ...localRule.value, year_month: Math.max(1, Math.min(12, parseInt(value) || 1)) };
  emit('update:modelValue', localRule.value);
};

const updateYearDay = value => {
  localRule.value = { ...localRule.value, year_day: Math.max(1, Math.min(31, parseInt(value) || 1)) };
  emit('update:modelValue', localRule.value);
};

const updateEndDate = value => {
  localRule.value = { ...localRule.value, end_date: value };
  emit('update:modelValue', localRule.value);
};

const updateEndCount = value => {
  localRule.value = { ...localRule.value, end_count: Math.max(1, parseInt(value) || 1) };
  emit('update:modelValue', localRule.value);
};

const openCustomModal = () => {
  emit('custom');
};

const clearRecurrence = () => {
  localRule.value = null;
  emit('update:modelValue', null);
};
</script>

<template>
  <div class="relative" v-on-click-outside="() => isOpen = false">
    <!-- Trigger -->
    <button
      type="button"
      class="flex items-center gap-2 w-full px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white hover:bg-n-surface-1 dark:hover:bg-n-surface-3 transition-colors"
      :class="{ 'bg-n-primary-50 border-n-primary-300 dark:bg-n-primary-900/20': localRule }"
      @click="isOpen = !isOpen"
    >
      <svg v-if="localRule" class="w-4 h-4 text-n-primary-600 dark:text-n-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      <span class="flex-1 text-left truncate">
        {{ localRule ? ruleDescription : t('RECURRENCE.NONE') }}
      </span>
      <svg class="w-4 h-4 text-n-text-light dark:text-n-text-muted transition-transform" :class="{ 'rotate-180': isOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Dropdown (opens upward) -->
    <div
      v-if="isOpen"
      class="absolute bottom-full mb-2 left-0 w-80 p-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl shadow-sm z-50"
    >
      <!-- Frequency Grid (3x2) -->
      <div class="grid grid-cols-3 gap-2 mb-4">
        <button
          v-for="opt in frequencyOptions"
          :key="opt.value"
          type="button"
          class="px-2 py-2.5 text-xs font-medium rounded-lg border transition-all text-center"
          :class="selectedFrequency === opt.value && localRule
            ? 'bg-n-primary-100 border-n-primary-300 text-n-primary-700 dark:bg-n-primary-900/30 dark:border-n-primary-700 dark:text-n-primary-300'
            : 'bg-white border-n-divider text-n-text dark:bg-n-surface-1 dark:border-white/10 dark:text-n-text-white hover:border-n-primary-300 dark:hover:border-n-primary-700'
          "
          @click="updateFrequency(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>

      <div v-if="localRule" class="space-y-4">
        <!-- Interval: "A cada [N] [unidade]" -->
        <div class="flex items-center gap-2">
          <span class="text-sm text-n-text-light dark:text-n-text-muted whitespace-nowrap">{{ t('RECURRENCE.EVERY') }}</span>
          <input
            type="number"
            min="1"
            :value="localRule.interval || 1"
            @input="updateInterval($event.target.value)"
            class="w-14 px-2 py-1.5 text-sm text-center border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
          />
          <select
            :value="selectedFrequency"
            @change="updateFrequency($event.target.value)"
            class="px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
          >
            <option v-for="opt in frequencyOptions" :key="opt.value" :value="opt.value">
              {{ opt.value === 'minutely' ? t('RECURRENCE.UNIT.MINUTES') :
                 opt.value === 'hourly' ? t('RECURRENCE.UNIT.HOURS') :
                 opt.value === 'daily' ? t('RECURRENCE.UNIT.DAYS') :
                 opt.value === 'weekly' ? t('RECURRENCE.UNIT.WEEKS') :
                 opt.value === 'monthly' ? t('RECURRENCE.UNIT.MONTHS') :
                 t('RECURRENCE.UNIT.YEARS') }}
            </option>
          </select>
        </div>

        <!-- Weekly: day circles -->
        <div v-if="selectedFrequency === 'weekly'" class="flex flex-wrap gap-1.5">
          <button
            v-for="day in weekDays"
            :key="day.value"
            type="button"
            class="w-9 h-9 text-xs font-medium rounded-full border transition-colors flex items-center justify-center"
            :class="localRule.week_days?.includes(day.value)
              ? 'bg-n-primary-100 border-n-primary-300 text-n-primary-700 dark:bg-n-primary-900/30 dark:border-n-primary-700 dark:text-n-primary-300'
              : 'bg-white border-n-divider text-n-text dark:bg-n-surface-1 dark:border-white/10 dark:text-n-text-white'
            "
            @click="updateWeekDays([
              ...(localRule.week_days || []).filter(d => d !== day.value),
              ...(localRule.week_days?.includes(day.value) ? [] : [day.value]),
            ])"
          >
            {{ day.label }}
          </button>
        </div>

        <!-- Monthly: type + day -->
        <div v-if="selectedFrequency === 'monthly'" class="space-y-2">
          <div class="flex gap-2">
            <button
              v-for="opt in monthlyTypeOptions"
              :key="opt.value"
              type="button"
              class="flex-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors text-center"
              :class="selectedMonthlyType === opt.value
                ? 'bg-n-primary-100 border-n-primary-300 text-n-primary-700 dark:bg-n-primary-900/30 dark:border-n-primary-700 dark:text-n-primary-300'
                : 'bg-white border-n-divider text-n-text dark:bg-n-surface-1 dark:border-white/10 dark:text-n-text-white'
              "
              @click="updateMonthlyType(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <div v-if="selectedMonthlyType === 'day_of_month'">
            <input
              type="number"
              min="1"
              max="31"
              :value="localRule.month_day || 1"
              @input="updateMonthDay($event.target.value)"
              class="w-full px-3 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
              :placeholder="t('RECURRENCE.DAY_OF_MONTH')"
            />
          </div>
          <div v-else class="flex gap-2">
            <select
              :value="localRule.monthly_week || 1"
              @change="updateMonthlyWeek($event.target.value)"
              class="flex-1 px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
            >
              <option value="1">{{ t('RECURRENCE.WEEKS.FIRST') }}</option>
              <option value="2">{{ t('RECURRENCE.WEEKS.SECOND') }}</option>
              <option value="3">{{ t('RECURRENCE.WEEKS.THIRD') }}</option>
              <option value="4">{{ t('RECURRENCE.WEEKS.FOURTH') }}</option>
              <option value="-1">{{ t('RECURRENCE.WEEKS.LAST') }}</option>
            </select>
            <select
              :value="localRule.monthly_weekday || 0"
              @change="updateMonthlyWeekday($event.target.value)"
              class="flex-1 px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
            >
              <option v-for="d in 7" :key="d-1" :value="d-1">{{ fullDayName(d-1) }}</option>
            </select>
          </div>
        </div>

        <!-- Yearly: month + day -->
        <div v-if="selectedFrequency === 'yearly'" class="flex gap-2">
          <select
            :value="localRule.year_month || 1"
            @change="updateYearMonth($event.target.value)"
            class="flex-1 px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
          >
            <option v-for="m in 12" :key="m" :value="m">{{ monthName(m-1) }}</option>
          </select>
          <input
            type="number"
            min="1"
            max="31"
            :value="localRule.year_day || 1"
            @input="updateYearDay($event.target.value)"
            class="w-20 px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
          />
        </div>

        <!-- End type -->
        <div class="flex gap-2">
          <button
            v-for="opt in endTypeOptions"
            :key="opt.value"
            type="button"
            class="flex-1 px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors text-center"
            :class="selectedEndType === opt.value
              ? 'bg-n-primary-100 border-n-primary-300 text-n-primary-700 dark:bg-n-primary-900/30 dark:border-n-primary-700 dark:text-n-primary-300'
              : 'bg-white border-n-divider text-n-text dark:bg-n-surface-1 dark:border-white/10 dark:text-n-text-white'
            "
            @click="updateEndType(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>

        <!-- End params -->
        <div v-if="selectedEndType === 'on_date'">
          <input
            type="date"
            :value="localRule.end_date"
            @change="updateEndDate($event.target.value)"
            class="w-full px-3 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
          />
        </div>
        <div v-if="selectedEndType === 'after_count'" class="flex items-center gap-2">
          <input
            type="number"
            min="1"
            :value="localRule.end_count || 1"
            @input="updateEndCount($event.target.value)"
            class="w-16 px-2 py-1.5 text-sm text-center border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-text dark:text-n-text-white focus:ring-2 focus:ring-n-primary-500 focus:border-transparent"
          />
          <span class="text-sm text-n-text-light dark:text-n-text-muted">{{ t('RECURRENCE.OCCURRENCES') }}</span>
        </div>

        <!-- Actions -->
        <div class="pt-3 border-t border-n-divider dark:border-white/10 flex gap-2">
          <button
            type="button"
            class="flex-1 px-3 py-2 text-sm font-medium text-n-text dark:text-n-text-white border border-n-divider rounded-lg hover:bg-n-surface-1 dark:hover:bg-n-surface-3 transition-colors"
            @click="clearRecurrence"
          >
            {{ t('RECURRENCE.REMOVE') }}
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-2 text-sm font-medium text-white bg-n-primary-600 rounded-lg hover:bg-n-primary-700 transition-colors"
            @click="openCustomModal"
          >
            {{ t('RECURRENCE.CUSTOM') }}
          </button>
        </div>
      </div>

      <!-- No rule: compact prompt -->
      <div v-else class="text-center py-4">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-white bg-n-primary-600 rounded-lg hover:bg-n-primary-700 transition-colors"
          @click="openCustomModal"
        >
          {{ t('RECURRENCE.CUSTOM') }}
        </button>
      </div>
    </div>
  </div>
</template>
